"use client";

import Link from "next/link";
import {useId, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import type {SavedOmegaWorkflow} from "@/services/wingman/omega-results";
import CompiledWorkflowPublicationOutcome from "./CompiledWorkflowPublicationOutcome";
import {workflowBuilderPath} from "@/utils/common/constants/routes";
import {confirmedDraftOf, type ChatWorkflowPublication, type ConfirmedWorkflowDraft} from "./useChatWorkflowPublication";
import {useCurrentChatWorkflow} from "./useCurrentChatWorkflow";
import Icon from "@/components/library/Icon";
import WorkflowCard, {WorkflowCardFooter} from "./WorkflowCard";
import {workflowPublicationSummary} from "./workflowPublicationSummary";

type Props = {
   workflow: SavedOmegaWorkflow;
   openInEditor: boolean;
   onOpenInEditor?: () => void;
   unavailable: boolean;
   onTest: (id: string) => Promise<boolean>;
   onPublish: (draft: ConfirmedWorkflowDraft) => Promise<ChatWorkflowPublication>;
};

type Publication = ChatWorkflowPublication | {status: "publishing"};

/** One saved workflow with the same summary and steps as its publication approval. */
export default function CompiledWorkflowCard({workflow: history, openInEditor, onOpenInEditor, unavailable, onTest, onPublish}: Props) {
   const {t, i18n} = useTranslation("ask");
   const {t: wt} = useTranslation("workflows");
   const {workflow, loading, failed, update, retry} = useCurrentChatWorkflow(history);
   const [testing, setTesting] = useState(false);
   const [testError, setTestError] = useState("");
   // Keep the reviewed workflow visible while its publication is in flight.
   const [publishingDraft, setPublishingDraft] = useState<ConfirmedWorkflowDraft>();
   const [publication, setPublication] = useState<Publication>();
   const publishDescriptionId = useId();
   const publishing = useRef(false);
   const isPublished = workflow.status === "published";
   const isPublishing = publication?.status === "publishing";
   const requestTest = async () => {
      setTesting(true);
      setTestError("");
      try {
         if (!(await onTest(workflow.id))) setTestError(t("workflowPanel.testNotStarted"));
      } catch {
         setTestError(t("workflowPanel.testNotStarted"));
      } finally {
         setTesting(false);
      }
   };

   const publish = async (draft: ConfirmedWorkflowDraft) => {
      if (publishing.current) return;
      publishing.current = true;
      setPublishingDraft(draft);
      setPublication({status: "publishing"});
      const result = await onPublish(draft).catch((): ChatWorkflowPublication => ({status: "publish_failed"}));
      setPublication(result);
      if (result.status === "published" || result.status === "already_published") {
         update({...confirmedDraftOf(result.workflow), status: "published"});
      }
      if (result.status === "stale") {
         if (result.current) update({...result.current, status: "draft"});
         else retry();
      }
      setPublishingDraft(undefined);
      publishing.current = false;
   };

   let statusKey = isPublished ? "workflowPanel.status.published" : "workflowPanel.status.draft";
   if (failed) statusKey = "workflowPanel.status.unavailable";
   if (loading) statusKey = "workflowPanel.status.checking";

   const reviewedWorkflow = publishingDraft ?? workflow;
   const summary = workflowPublicationSummary(reviewedWorkflow, t, wt, i18n.language);
   if (isPublishing) statusKey = "workflowPanel.confirm.publishing";
   let visualState: "approved" | "failed" | "completed" | undefined;
   if (isPublished) visualState = "completed";
   if (failed) visualState = "failed";
   if (isPublishing || loading) visualState = "approved";

   return (
      <WorkflowCard workflow={reviewedWorkflow} summary={summary} status={t(statusKey)} state={visualState}>
         <WorkflowCardFooter>
            <p>{t("workflowPanel.testHint")}</p>
            {workflow.status === "draft" && <p id={publishDescriptionId}>{summary.consequence}</p>}
            <div className="flex flex-wrap items-center gap-2">
               {openInEditor && <Link className="approval-open inline-flex items-center gap-1.5" href={workflowBuilderPath(workflow.id)} onNavigate={onOpenInEditor}>
                  {t("workflowPanel.openInEditor")}<Icon icon="arrow-up-right-from-square" className="h-3 w-3" />
               </Link>}
               <div className="ml-auto flex flex-wrap justify-end gap-2">
                  <button type="button" className={isPublished ? "approval-approve" : "approval-reject"} disabled={unavailable || loading || failed || isPublishing || testing} onClick={() => void requestTest()}>
                     {t(testing ? "workflowPanel.testing" : "workflowPanel.test")}
                  </button>
                  {workflow.status === "draft" && (
                     <button
                        type="button"
                        className="approval-approve"
                        aria-describedby={publishDescriptionId}
                        disabled={unavailable || isPublishing || testing || loading || failed || !Number.isInteger(Number(workflow.revision))}
                        onKeyDown={event => {
                           if (event.repeat && (event.key === "Enter" || event.key === " ")) event.preventDefault();
                        }}
                        onClick={event => {
                           if (event.detail > 1) return;
                           void publish(confirmedDraftOf(workflow));
                        }}
                     >
                        {t(isPublishing ? "workflowPanel.confirm.publishing" : "workflowPanel.publish")}
                     </button>
                  )}
               </div>
            </div>
            {testError && <p role="alert">{testError}</p>}
            {failed && <div className="space-y-2">
               <p role="alert">{t("workflowPanel.publication.lookupFailed")}</p>
               <button type="button" className="approval-reject" onClick={retry}>{t("workflowPanel.retryStatus")}</button>
            </div>}
            {publication && publication.status !== "publishing" && <CompiledWorkflowPublicationOutcome publication={publication} />}
         </WorkflowCardFooter>
      </WorkflowCard>
   );
}
